import { getEntitySubdomain } from '@opengov/app-core/entity/selectors';
import { createSelector } from '@opengov/compose-app/reselect';
import { localeSortByKey } from '@opengov/locale';
import { selectOgqlViews, selectOgqlViewsLoading } from '@opengov/ogql-client/selectors';

import { isErpDatasetLens, isV2CoaLens, isV3CoaLens, IReportDatasetLensResponse } from '@opengov/reporting-types';
import { ReportTypes } from '../../../common/forms/reportForm/reportTypes';
import { reportLocaleText } from '../../../common/locale';
import { selectEntityLenses, selectIsLoadingEntityLenses } from '../../../lenses/lensSelectors';
import { selectNewReport, selectNewReportTypeState } from '../../selectors/selectReportCreateState';
import { reportCreateActionCreators } from '../../state/reportCreateState';
import {
  selectMultiHierarchyCoaFinancialReportsFlag,
  selectShowViewsInsteadOfLensesOnReportCreationFlag,
  selectShowBothViewsAndLensesOnReportCreationFlag
} from '../../../common/flags';
import { List } from 'immutable';

export const selectViews = createSelector(
  selectOgqlViews,
  selectEntityLenses,
  selectShowBothViewsAndLensesOnReportCreationFlag,
  selectShowViewsInsteadOfLensesOnReportCreationFlag,
  (views, datasetLenses, showBothViewsAndLenses, showViewsInsteadOfLenses) => {
    const lensesToShow = datasetLenses.filter((lens) => !isErpDatasetLens(lens));
    // Legacy behavior to show both views and lenses to be maintained during migration
    if (showBothViewsAndLenses) {
      const allViews = views.map((view) => {
        return (view as any).set('isLens', false);
      });
      return allViews
        .filter((view) => datasetLenses.find((lens) => lens.sourceId.includes(view.id)) === undefined)
        .concat(
          lensesToShow.map((lens) => {
            return {
              ...lens,
              isLens: true
            };
          })
        )
        .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
    }

    const viewsToDisplay = showViewsInsteadOfLenses
      ? views.map((view) => (view as any).set('isLens', false))
      : List(lensesToShow.map((lens) => ({ ...lens, isLens: true })));

    return viewsToDisplay.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
  }
);

const selectVisibleViews = createSelector(selectViews, (views) => {
  // Dataset lenses have a visible property, Views do not. We're explicitly
  // filtering out `visible:false` views in order to ensure views
  // are not filtered out. (visible: undefined is considered visible)
  return views.filter((view) => view.visible !== false);
});

export const selectReportCreateDatasetProps = createSelector(
  getEntitySubdomain,
  selectNewReport,
  selectOgqlViewsLoading,
  selectIsLoadingEntityLenses,
  reportLocaleText,
  selectNewReportTypeState,
  selectVisibleViews,
  selectMultiHierarchyCoaFinancialReportsFlag,
  (entitySubdomain, newReport, viewsLoading, datasetLensesLoading, localeText, reportType, views, isMultiHierarchy) => {
    const isCoAReport = reportType && reportType === ReportTypes.AccountsSummary;

    return {
      entitySubdomain,
      newReport,
      views: views.sort(localeSortByKey('name')),
      viewsLoading: datasetLensesLoading || viewsLoading,
      reportLocaleText: localeText,
      onReportSourceChange: (source, wizardComponentProps) => {
        if (isMultiHierarchy) {
          reportCreateActionCreators.updateReportLensId(source.id);
          const lens = source as IReportDatasetLensResponse;
          const isCoaDataset = isV2CoaLens(lens) || isV3CoaLens(lens);
          reportCreateActionCreators.updateReportType(
            isCoaDataset ? ReportTypes.MultiDataset : ReportTypes.ChartAndDetails
          );
        } else {
          if (isCoAReport) {
            reportCreateActionCreators.updateReportLensId(source.id);
          } else if (source.isLens) {
            reportCreateActionCreators.updateReportLensId(source.id);
          } else {
            reportCreateActionCreators.updateReportViewId(source.id);
          }
          // When a lens is selected we add a third step for selecting report type
          if (source.isLens) wizardComponentProps.setNumberOfSteps(3);
        }
        wizardComponentProps.incrementStep();
      }
    };
  }
);
